import { AnimatedElement } from "home/Steps";
import { cx } from "lib/cx";
import { useIntersectionObserver } from "lib/hooks/useIntersectionObserver";
import Image from "next/image";
import React from "react";

const resumeWritingSteps = [
  {
    id: 1,
    title: "Upload Your Resume",
    description:
      "Let our AI scan and auto-fill your resume information to save you time.",
    imageSize:
      "w-[100px] h-[80px] md:w-[116px] md:h-[97px] lg:h-[197px] lg:w-[216px]",
  },
  {
    id: 2,
    title: "Consultation",
    description:
      "Receive the call form our executive within one day and express your expectations and provide necessary information.",
    imageSize:
      "w-[88px] h-[80px] md:w-[178px] md:h-[123px] lg:h-[182px] lg:w-[273px]",
  },
  {
    id: 3,
    title: "Resume Writing",
    description:
      "Our inhouse professionals will get their hand on your resume and create an enhanced one with their experties.",
    imageSize:
      "w-[120px] h-[90px] md:w-[220px] md:h-[150px] lg:h-[220px] lg:w-[324px]",
  },
  {
    id: 4,
    title: "Get your Resume",
    description:
      "Find your resumes in your dashboard within 2-3 business days with edit access.",
    imageSize:
      "w-[83px] h-[110px] md:w-[133px] md:h-[160px] lg:h-[268px] lg:w-[233px]",
  },
];

const Steps = () => {
  return (
    <section className="px-4 font-montserrat md:px-8">
      {/* Heading */}
      <div className="mx-auto mb-10 flex flex-col text-center text-[18px] font-medium text-[#071A34] md:text-[20.4px] lg:text-[27.2px] xl:text-[34px] 2xl:text-[40.8px]">
        How it Works?
        <span className="-mt-1 text-xs text-[#375AE5] md:-mt-2 md:text-sm lg:text-lg xl:text-[24px] 2xl:text-[30px]">
          Simple Steps
        </span>
      </div>

      {/* Steps */}
      {resumeWritingSteps.map((step, index) => (
        <Step
          key={step.id}
          id={step.id}
          title={step.title}
          description={step.description}
          imageSize={step.imageSize}
        />
      ))}
    </section>
  );
};

export default Steps;

const Step = ({
  id,
  title,
  description,
  imageSize,
}: {
  id: number;
  title: string;
  description: string;
  imageSize: string;
}) => {
  const [isVisible, ref] = useIntersectionObserver<HTMLDivElement>(0.9);
  const isEven = id % 2 === 0;
  return (
    <div className="relative pt-4 md:pt-16" ref={ref}>
      <div className="absolute left-1/2 top-0 hidden h-full w-[1px] -translate-x-1/2 border border-dashed border-gray-300 md:block" />
      <div
        className={cx(
          "absolute left-1/2 top-[60%] hidden h-1/4 w-[1px] -translate-x-1/2 md:block lg:top-[55%] xl:top-1/2 2xl:top-[58%]",
          isVisible && "rounded-lg border-2 border-[#375AE5]"
        )}
      />
      <div
        className={cx(
          "mx-auto mt-8 flex w-[100%] items-center justify-between md:mt-12 md:w-[700px] md:justify-evenly lg:mt-16 lg:w-[900px] xl:mt-0 xl:w-[1000px] 2xl:mt-24",
          isEven ? "flex-row-reverse" : "flex-row"
        )}
      >
        <AnimatedElement slideFrom={isEven ? "right" : "left"}>
          <StepGraphic id={id} imageSize={imageSize} />
        </AnimatedElement>
        <AnimatedElement slideFrom={!isEven ? "right" : "left"}>
          <StepInfo
            id={id}
            title={title}
            description={description}
            isVisible={isVisible}
          />
        </AnimatedElement>
      </div>
    </div>
  );
};

const StepGraphic = ({ id, imageSize }: { id: number; imageSize: string }) => {
  return (
    <div className={`relative ${imageSize}`}>
      <Image
        src={`/assets/resume-writing/writing-steps/step${id}.png`}
        fill
        alt="Step ${id}"
        style={{ objectFit: "cover" }}
      />
    </div>
  );
};

const StepInfo = ({
  id,
  title,
  description,
  isVisible,
}: {
  id: number;
  title: string;
  description: string;
  isVisible?: boolean;
}) => {
  const isEvenStep = id % 2 === 0;
  return (
    <div
      className={cx(
        "flex items-center gap-4",
        isEvenStep && "flex-row-reverse",
        id === 1 && "md:-mr-40",
        id === 2 && "md:-ml-32",
        id === 3 && "md:-mr-[106px]",
        id === 4 && "md:-ml-[150px]"
      )}
    >
      <div
        className={cx(
          "relative hidden h-4 w-4 items-center justify-center rounded-full bg-[#C7C7C7] text-xs font-semibold text-white md:flex md:h-7 md:w-7 md:text-lg lg:h-9 lg:w-9 lg:text-xl"
        )}
        style={{ backgroundColor: isVisible ? "#375AE5" : "#C7C7C7" }}
      >
        {id}
        <div
          className={cx(
            "w-10 md:w-[88px] lg:w-20 xl:w-24",
            "absolute h-[1px] border border-dashed",
            isEvenStep
              ? "left-0 md:translate-x-6 lg:translate-x-9"
              : "right-0 md:-translate-x-6 lg:-translate-x-9",
            isVisible ? "border-[#375AE5]" : "border-gray-300"
          )}
        ></div>
      </div>
      <div className="steps-shadow w-44 rounded-md p-2 text-left md:w-56 md:px-6 md:py-4 lg:w-80">
        <h3 className="text-xs font-semibold lg:text-base xl:text-xl 2xl:text-2xl">
          {title}
        </h3>
        <p className="mt-2 text-[9px] font-normal md:mt-4 md:text-[9.6px] lg:text-[12.8px] xl:text-base 2xl:text-xl">
          {description}
        </p>
      </div>
    </div>
  );
};
